import React, { useState, useEffect } from "react";
import "./ClassManagement.css";
import {
  FaChalkboardTeacher,
  FaArrowLeft,
  FaPlus,
  FaUsers,
  FaDoorOpen,
  FaUserTie,
  FaEdit,
  FaTimes,
  FaCheck,
} from "react-icons/fa";

const ClassManagement = ({ token, onBack }) => {
  const [classes, setClasses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState({
    className: "",
    section: "A",
    teacher: "",
    capacity: 20,
    roomNo: "",
  });

  useEffect(() => {
    // Simulated fetch for classes list
    // Replace with: apiService.getClasses(token)
    const fetchClasses = async () => {
      try {
        setLoading(true);
        setTimeout(() => {
          const mockClasses = [
            {
              id: 1,
              className: "Playgroup",
              section: "A",
              teacher: "Ms. Sunita Rao",
              enrolled: 12,
              capacity: 15,
              roomNo: "Room 101",
            },
            {
              id: 2,
              className: "Nursery",
              section: "A",
              teacher: "Ms. Priya Sharma",
              enrolled: 18,
              capacity: 20,
              roomNo: "Room 102",
            },
            {
              id: 3,
              className: "Nursery",
              section: "B",
              teacher: "Ms. Kavita Nair",
              enrolled: 15,
              capacity: 20,
              roomNo: "Room 103",
            },
            {
              id: 4,
              className: "LKG",
              section: "A",
              teacher: "Ms. Anjali Mehta",
              enrolled: 22,
              capacity: 25,
              roomNo: "Room 201",
            },
            {
              id: 5,
              className: "UKG",
              section: "A",
              teacher: "Ms. Pooja Verma",
              enrolled: 20,
              capacity: 25,
              roomNo: "Room 202",
            },
          ];
          setClasses(mockClasses);
          setLoading(false);
        }, 500);
      } catch (error) {
        console.error("Error loading classes:", error);
        setLoading(false);
      }
    };

    fetchClasses();
  }, [token]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleAddClassSubmit = (e) => {
    e.preventDefault();
    const newClassObj = {
      id: Date.now(),
      className: formData.className,
      section: formData.section,
      teacher: formData.teacher || "Unassigned",
      enrolled: 0,
      capacity: parseInt(formData.capacity, 10) || 20,
      roomNo: formData.roomNo || "TBD",
    };

    setClasses((prev) => [...prev, newClassObj]);
    setShowModal(false);
    setFormData({
      className: "",
      section: "A",
      teacher: "",
      capacity: 20,
      roomNo: "",
    });
  };

  return (
    <div className="class-mgmt-container">
      {/* Header Bar */}
      <div className="class-mgmt-header">
        <div className="header-left">
          <button className="back-btn" onClick={onBack} type="button">
            <FaArrowLeft /> Back to Dashboard
          </button>
          <h2>
            <FaChalkboardTeacher className="header-icon" /> Class & Section Management
          </h2>
        </div>
        <button
          className="btn-add-class"
          onClick={() => setShowModal(true)}
          type="button"
        >
          <FaPlus /> Add New Class
        </button>
      </div>

      {/* Class Overview Grid */}
      {loading ? (
        <div className="class-loading">
          <div className="spinner"></div>
          <p>Loading Class Roster...</p>
        </div>
      ) : (
        <div className="classes-grid">
          {classes.map((cls) => {
            const isFull = cls.enrolled >= cls.capacity;
            const occupancyPct = Math.min(
              100,
              Math.round((cls.enrolled / cls.capacity) * 100)
            );

            return (
              <div key={cls.id} className="class-card">
                <div className="card-header">
                  <div className="class-title-group">
                    <h3>
                      {cls.className} - {cls.section}
                    </h3>
                    <span className="room-badge">
                      <FaDoorOpen /> {cls.roomNo}
                    </span>
                  </div>
                  <button className="btn-icon-edit" title="Edit Class Details">
                    <FaEdit />
                  </button>
                </div>

                <div className="card-body">
                  <div className="detail-item">
                    <span className="label">
                      <FaUserTie /> Class Teacher:
                    </span>
                    <span className="value">{cls.teacher}</span>
                  </div>

                  <div className="detail-item">
                    <span className="label">
                      <FaUsers /> Enrollment Status:
                    </span>
                    <span className="value">
                      {cls.enrolled} / {cls.capacity} Students
                    </span>
                  </div>

                  {/* Capacity Bar */}
                  <div className="progress-container">
                    <div
                      className={`progress-bar ${isFull ? "full" : ""}`}
                      style={{ width: `${occupancyPct}%` }}
                    ></div>
                  </div>
                  <div className="progress-labels">
                    <span>{occupancyPct}% Occupied</span>
                    {isFull && <span className="tag-full">FULL</span>}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Add Class Modal */}
      {showModal && (
        <div className="modal-overlay">
          <div className="modal-card">
            <div className="modal-header">
              <h3>Add New Class / Section</h3>
              <button
                className="close-btn"
                onClick={() => setShowModal(false)}
                type="button"
              >
                <FaTimes />
              </button>
            </div>

            <form onSubmit={handleAddClassSubmit} className="modal-form">
              <div className="form-group">
                <label>Program / Grade Name *</label>
                <select
                  name="className"
                  value={formData.className}
                  onChange={handleInputChange}
                  required
                >
                  <option value="">Select Grade</option>
                  <option value="Playgroup">Playgroup</option>
                  <option value="Nursery">Nursery</option>
                  <option value="LKG">LKG</option>
                  <option value="UKG">UKG</option>
                  <option value="Daycare">Daycare</option>
                </select>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Section *</label>
                  <input
                    type="text"
                    name="section"
                    value={formData.section}
                    onChange={handleInputChange}
                    placeholder="e.g. A"
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Max Capacity *</label>
                  <input
                    type="number"
                    name="capacity"
                    value={formData.capacity}
                    onChange={handleInputChange}
                    min="1"
                    max="50"
                    required
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Assigned Teacher</label>
                <input
                  type="text"
                  name="teacher"
                  value={formData.teacher}
                  onChange={handleInputChange}
                  placeholder="Teacher's Full Name"
                />
              </div>

              <div className="form-group">
                <label>Room Number / Location</label>
                <input
                  type="text"
                  name="roomNo"
                  value={formData.roomNo}
                  onChange={handleInputChange}
                  placeholder="e.g. Room 104"
                />
              </div>

              <div className="modal-actions">
                <button
                  type="button"
                  className="btn-cancel"
                  onClick={() => setShowModal(false)}
                >
                  Cancel
                </button>
                <button type="submit" className="btn-submit">
                  <FaCheck /> Create Class
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ClassManagement;